import type { components } from '@multaqa/api-client'
import { DEFAULT_LOCALE, i18next, isLocale, useLocale } from '@multaqa/i18n'
import { type QueryClient, queryOptions, useQuery } from '@tanstack/react-query'
import { api } from './api'
import { type Me, meQuery } from './me'

/**
 * Which sheets this user may import. The API decides each one (the company's
 * licence, every permission a sheet needs, the company door) and returns the
 * list. The route guard and the Import actions only read that list, so this
 * file holds no copy of the rules.
 */

export type DataImportSection = components['schemas']['DataImportSectionResource']

/**
 * The sheets the company door offers for one company and locale. The API
 * translates each label, so the locale is part of the key. Cached, so a list
 * page does not ask again on every mount.
 */
export function importSectionsQuery(companyId: string, locale: string) {
  return queryOptions({
    queryKey: ['core', 'companies', companyId, 'data-imports', 'sections', locale],
    staleTime: 30_000,
    queryFn: async (): Promise<DataImportSection[]> => {
      const response = await api.GET('/core/companies/{company}/data-imports/sections', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { company: companyId } },
      })
      if (!response.data) throw new Error('GET data-imports/sections returned no body')
      return response.data.data
    },
  })
}

/** The sections of the active company; none is fetched while the company is unknown. */
export function useImportSections(companyId: string | null) {
  const { locale } = useLocale()
  return useQuery({
    ...importSectionsQuery(companyId ?? '', locale),
    enabled: companyId !== null,
  })
}

/** Whether the API offered this sheet. False until the sections have loaded. */
export function canImportSheet(
  sections: readonly DataImportSection[] | undefined,
  sheet: string,
): boolean {
  return sections?.some((section) => section.key === sheet) ?? false
}

/**
 * The route guard's answer: whether the API offers this user any sheet. It
 * uses `fetchQuery`, which re-runs a stale query, and an invalidated query is
 * always stale. `ensureQueryData` would return the cached list instead. `/me`
 * is already cached, because `_authenticated` resolves it before this runs.
 */
export async function mayUseDataImport(queryClient: QueryClient): Promise<boolean> {
  const companyId = queryClient.getQueryData<Me>(meQuery.queryKey)?.active_company_id ?? null
  if (companyId === null) return false
  const locale = isLocale(i18next.language) ? i18next.language : DEFAULT_LOCALE
  const sections = await queryClient.fetchQuery(importSectionsQuery(companyId, locale))
  return sections.length > 0
}
